import {
    Block,
    Close as CloseIcon,
    Comment as CommentIcon,
    ContentCopy as CopyIcon,
    Delete,
    Flag as FlagIcon,
    MoneyOff,
} from '@mui/icons-material';
import { Button, ButtonGroup, Box, IconButton, Paper, Slide, Stack, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Transaction, TransactionType, TransactionTypeGenericType } from '../../../services/transaction.types';
import {
    canBulkMarkAsNonTaxable,
    canBulkMarkAsNotSpam,
    canBulkMarkAsSpam,
    canBulkMarkAsTaxable,
} from '../utils/transactionUtils';
import ChangeTypeAction from './ChangeTypeAction';

interface BulkActionBarProps {
    selectedIds: number[];
    selectedTransactionGenericTypes: TransactionTypeGenericType | null;
    onDelete: () => void;
    onMarkAsSpam: () => void;
    onMarkNonTaxable: () => void;
    onMarkAsNotSpam: () => void;
    onMarkTaxable: () => void;
    onCopy?: () => void;
    onAddNote?: () => void;
    onResolveIssues?: () => void;
    onClearSelection: () => void;
    onTypeChange: (typeIds: number) => void;
    types: TransactionType[];
    hasIssues?: boolean;
    selectedTransactions: Transaction[];
}

const BulkActionBar: React.FC<BulkActionBarProps> = ({
    selectedIds,
    selectedTransactionGenericTypes,
    onDelete,
    onMarkAsSpam,
    onMarkNonTaxable,
    onMarkAsNotSpam,
    onMarkTaxable,
    onCopy,
    onAddNote,
    onResolveIssues,
    onClearSelection,
    onTypeChange,
    types,
    hasIssues,
    selectedTransactions,
}) => {
    const { t } = useTranslation();
    const isVisible = selectedIds.length > 0;
    const canEditType =
        selectedTransactionGenericTypes !== null &&
        selectedTransactionGenericTypes !== TransactionTypeGenericType.Mixed;

    return (
        <Slide direction="up" in={isVisible} mountOnEnter unmountOnExit>
            <Box sx={{ 
                position: 'fixed',
                bottom: 16,
                left: 0,
                right: 0,
                display: 'flex',
                justifyContent: 'center',
                zIndex: 1100,
                // Apply a slight offset to account for the sidebar
                '& > .MuiPaper-root': {
                    marginLeft: { xs: 0, sm: '-40px', md: '-80px', lg: '-100px' }
                }
            }}>
                <Paper 
                    elevation={0} 
                    sx={{
                        width: 'fit-content',
                        maxWidth: '100%',
                        p: 1,
                        borderRadius: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 1,
                        bgcolor: (theme) => theme.palette.background.paper,
                        border: '1px solid',
                        borderColor: (theme) => theme.palette.divider,
                        boxShadow: (theme) => `0px 5px 15px ${theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
                    }}
                >
                    <Stack
                        direction={{ xs: 'column', sm: 'column', md: 'row' }}
                        spacing={2}
                        alignItems={{ xs: 'stretch', sm: 'stretch', md: 'center' }}
                        sx={{
                            maxHeight: { xs: '50vh', sm: '50vh', md: 'none' },
                            overflowY: { xs: 'auto', sm: 'auto', md: 'visible' },
                            width: { xs: '100%', sm: '100%', md: 'auto' },
                            p: { xs: 1, sm: 1, md: 0 },
                        }}
                    >
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2" sx={{
                                fontWeight: 500,
                                color: 'text.primary'
                            }}>
                                {t('transactions.bulkActions.selectedCount', { 
                                    count: selectedIds.length,
                                    plural: selectedIds.length > 1,
                                     })}
                            </Typography>
                            <Tooltip title={t('transactions.bulkActions.clearSelection')}>
                                <IconButton
                                    size="small"
                                    onClick={onClearSelection}
                                    sx={{
                                        color: 'text.secondary',
                                        '&:hover': {
                                            color: 'primary.main'
                                        }
                                    }}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Stack>

                        <Stack
                            direction={{ xs: 'column', sm: 'column', md: 'row' }}
                            spacing={1}
                            sx={{ width: { xs: '100%', sm: '100%', md: 'auto' } }}
                        >
                            {canEditType && selectedTransactionGenericTypes !== TransactionTypeGenericType.Trade && (
                                <ChangeTypeAction
                                    types={types}
                                    onTypeChange={onTypeChange}
                                    selectedTransactions={selectedTransactions}
                                    selectedTransactionGenericTypes={selectedTransactionGenericTypes}
                                />
                            )}
                            {onCopy && (
                                <Button startIcon={<CopyIcon />} onClick={onCopy}>
                                    {t('transactions.bulkActions.copy')}
                                </Button>
                            )}
                            {onAddNote && (
                                <Button startIcon={<CommentIcon />} onClick={onAddNote}>
                                    {t('transactions.bulkActions.addNote')}
                                </Button>
                            )}
                            {hasIssues && onResolveIssues && (
                                <Button startIcon={<FlagIcon />} onClick={onResolveIssues}>
                                    {t('transactions.bulkActions.resolveIssues')}
                                </Button>
                            )}
                            {canBulkMarkAsSpam(selectedTransactions) && (
                                <Button startIcon={<Block />} onClick={onMarkAsSpam}>
                                    {t('transactions.bulkActions.markAsSpam')}
                                </Button>
                            )}
                            {canBulkMarkAsNotSpam(selectedTransactions) && (
                                <Button startIcon={<Block />} onClick={onMarkAsNotSpam}>
                                    {t('transactions.bulkActions.removeFromSpam')}
                                </Button>
                            )}
                            {canEditType && canBulkMarkAsNonTaxable(selectedTransactions) && (
                                <Button startIcon={<MoneyOff />} onClick={onMarkNonTaxable}>
                                    {t('transactions.bulkActions.nonTaxable')}
                                </Button>
                            )}
                            {canEditType && canBulkMarkAsTaxable(selectedTransactions) && (
                                <Button startIcon={<MoneyOff />} onClick={onMarkTaxable}>
                                    {t('transactions.bulkActions.taxable')}
                                </Button>
                            )}
                            <Button startIcon={<Delete />} onClick={onDelete}>
                                {t('transactions.bulkActions.delete')}
                            </Button>
                        </Stack>
                    </Stack>
                </Paper>
            </Box>
        </Slide>
    );
};

export default BulkActionBar;
